import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ShieldAlert, ChevronRight, Home, BarChart3, FileText } from "lucide-react";

export default function NotFound() {
  const navigate  = useNavigate();
  const token     = localStorage.getItem("token");

  return (
    <div className="min-h-screen bg-[#0f172a] font-['Inter'] flex items-center
      justify-center px-6 relative overflow-hidden">

      {/* Background glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2
        w-[600px] h-[600px] bg-red-600 opacity-[0.04] rounded-full blur-[120px]
        pointer-events-none" />

      {/* Top left logo */}
      <div className="absolute top-8 left-8 flex items-center gap-3 cursor-pointer"
        onClick={() => navigate("/")}>
        <div className="w-9 h-9 bg-red-600 rounded-xl flex items-center justify-center
          shadow-lg shadow-red-900/40">
          <span className="text-white font-black text-base">V</span>
        </div>
        <span className="text-white font-black text-lg tracking-tighter uppercase">
          VicShield
        </span>
      </div>

      <div className="relative z-10 text-center max-w-lg">

        {/* 404 */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="flex items-center justify-center mb-6">
            <div className="w-20 h-20 bg-red-600/10 border border-red-600/20
              rounded-[2rem] flex items-center justify-center">
              <ShieldAlert size={40} className="text-red-500" />
            </div>
          </div>

          <p className="text-[10px] font-black uppercase tracking-[0.4em]
            text-red-500 mb-4">
            Error 404
          </p>

          <h1 className="text-6xl font-black text-white tracking-tighter uppercase mb-4">
            Page Not<br />
            <span className="text-red-600">Found.</span>
          </h1>

          <p className="text-slate-400 font-medium leading-relaxed mb-10">
            The intelligence you are looking for does not exist or has been
            moved. Check the URL or navigate back to safety.
          </p>
        </motion.div>

        {/* Quick links */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="space-y-3"
        >
          {token ? (
            <>
              <button
                onClick={() => navigate("/dashboard")}
                className="w-full flex items-center justify-between px-6 py-4
                  bg-white/5 border border-white/10 rounded-2xl hover:bg-red-600/10
                  hover:border-red-600/30 transition-all group"
              >
                <div className="flex items-center gap-3">
                  <Home size={18} className="text-slate-400 group-hover:text-red-400" />
                  <span className="text-sm font-black text-white uppercase tracking-wide">
                    Back to Dashboard
                  </span>
                </div>
                <ChevronRight size={16} className="text-slate-600 group-hover:text-red-400" />
              </button>

              <button
                onClick={() => navigate("/assessment")}
                className="w-full flex items-center justify-between px-6 py-4
                  bg-white/5 border border-white/10 rounded-2xl hover:bg-white/10
                  transition-all group"
              >
                <div className="flex items-center gap-3">
                  <BarChart3 size={18} className="text-slate-400" />
                  <span className="text-sm font-black text-white uppercase tracking-wide">
                    Run Assessment
                  </span>
                </div>
                <ChevronRight size={16} className="text-slate-600" />
              </button>

              <button
                onClick={() => navigate("/reports")}
                className="w-full flex items-center justify-between px-6 py-4
                  bg-white/5 border border-white/10 rounded-2xl hover:bg-white/10
                  transition-all group"
              >
                <div className="flex items-center gap-3">
                  <FileText size={18} className="text-slate-400" />
                  <span className="text-sm font-black text-white uppercase tracking-wide">
                    View Reports
                  </span>
                </div>
                <ChevronRight size={16} className="text-slate-600" />
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => navigate("/")}
                className="w-full flex items-center justify-between px-6 py-4
                  bg-red-600 rounded-2xl hover:bg-red-700 transition-all group"
              >
                <div className="flex items-center gap-3">
                  <Home size={18} className="text-white" />
                  <span className="text-sm font-black text-white uppercase tracking-wide">
                    Back to Home
                  </span>
                </div>
                <ChevronRight size={16} className="text-white" />
              </button>

              <button
                onClick={() => navigate("/login")}
                className="w-full flex items-center justify-between px-6 py-4
                  bg-white/5 border border-white/10 rounded-2xl hover:bg-white/10
                  transition-all group"
              >
                <div className="flex items-center gap-3">
                  <ShieldAlert size={18} className="text-slate-400" />
                  <span className="text-sm font-black text-white uppercase tracking-wide">
                    Sign In
                  </span>
                </div>
                <ChevronRight size={16} className="text-slate-600" />
              </button>
            </>
          )}
        </motion.div>

        {/* Contact */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="mt-10 text-[10px] font-bold text-slate-600 uppercase tracking-widest"
        >
          Need help? Contact{" "}
          <a href="mailto:support@vicshield.com"
            className="text-red-500 hover:text-red-400 transition-colors">
            support@vicshield.com
          </a>
        </motion.p>
      </div>
    </div>
  );
}
